import React, { useState } from 'react';
import { X } from 'lucide-react';

const AuthModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [view, setView] = useState('login'); // 'login', 'signup', 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Need to track this so we know who to verify OTP against
  const [preAuthToken, setPreAuthToken] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    setView('login');
    setEmail('');
    setPassword('');
    setName('');
    setOtp('');
    setError('');
    onClose();
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:3001/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Since signup requires email verification, we could redirect to OTP
      // But for simplicity of this flow based on the backend, signup doesn't give a preAuthToken
      // We instruct user to login.
      setView('login');
      setError('Signup successful. Please login to verify your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:3001/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      setPreAuthToken(data.preAuthToken);
      setView('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:3001/api/auth/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${preAuthToken}`
        },
        body: JSON.stringify({ otp })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid OTP');
      }

      // Success! Save final token
      localStorage.setItem('accessToken', data.accessToken);
      onLoginSuccess();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="relative w-full max-w-4xl bg-[#111111] rounded-3xl overflow-hidden flex shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-[#333] animate-in fade-in zoom-in duration-300">

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Form Section - Left Half */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center bg-[#111111] min-h-[500px]">

          <div className="max-w-sm mx-auto w-full">
            {view === 'login' && (
              <>
                <h2 className="text-2xl font-light text-white mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Welcome back</h2>
                <p className="text-xs text-gray-400 mb-8 lowercase tracking-wide">Enter your credentials to access your account</p>

                {error && <div className="mb-4 text-xs text-[#c8a96e] bg-[#c8a96e]/10 border border-[#c8a96e]/20 p-3 rounded">{error}</div>}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#4B5E38] focus:ring-1 focus:ring-[#4B5E38]/20 transition-all placeholder:text-gray-300"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-[10px] uppercase tracking-wider text-gray-400">Password</label>
                      <button type="button" className="text-[10px] text-[#c8a96e] hover:underline">Forgot password?</button>
                    </div>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      className="w-full px-3 py-2 text-sm bg-[#1a1a1a] text-white border border-[#333] rounded-lg focus:outline-none focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e]/20 transition-all placeholder:text-gray-600"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1 pb-2">
                    <input type="checkbox" id="remember" className="rounded border-[#333] bg-[#1a1a1a] text-[#c8a96e] focus:ring-[#c8a96e]/20" />
                    <label htmlFor="remember" className="text-[10px] text-gray-400">Remember for 30 days</label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 bg-[#c8a96e] text-[#111] font-medium text-xs tracking-wider rounded-lg hover:bg-[#b0945b] transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '...' : 'Login'}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-600 before:flex-1 before:h-[1px] before:bg-white/10 after:flex-1 after:h-[1px] after:bg-white/10">
                  or
                </div>

                <div className="flex gap-3 mt-6">
                  <button className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#333] bg-[#1a1a1a] text-white rounded-lg text-[11px] hover:bg-white/5 transition-colors">
                    <span className="font-bold text-white">G</span> Google
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#333] bg-[#1a1a1a] text-white rounded-lg text-[11px] hover:bg-white/5 transition-colors">
                    <span className="text-white text-lg leading-none mb-1"></span> Apple
                  </button>
                </div>

                <p className="mt-8 text-center text-xs text-gray-500">
                  Don't have an account? <button onClick={() => { setView('signup'); setError('') }} className="text-[#c8a96e] hover:underline font-medium">Sign Up</button>
                </p>
              </>
            )}

            {view === 'signup' && (
              <>
                <h2 className="text-2xl font-light text-white mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>Get Started</h2>

                {error && <div className="mb-4 text-xs text-[#c8a96e] bg-[#c8a96e]/10 border border-[#c8a96e]/20 p-3 rounded">{error}</div>}

                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Name</label>
                    <input
                      type="text"
                      required
                      placeholder="Enter your name"
                      className="w-full px-3 py-2 text-sm bg-[#1a1a1a] text-white border border-[#333] rounded-lg focus:outline-none focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e]/20 transition-all placeholder:text-gray-600"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 text-sm bg-[#1a1a1a] text-white border border-[#333] rounded-lg focus:outline-none focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e]/20 transition-all placeholder:text-gray-600"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Password</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      className="w-full px-3 py-2 text-sm bg-[#1a1a1a] text-white border border-[#333] rounded-lg focus:outline-none focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e]/20 transition-all placeholder:text-gray-600"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1 pb-2">
                    <input type="checkbox" id="terms" required className="rounded border-[#333] bg-[#1a1a1a] text-[#c8a96e] focus:ring-[#c8a96e]/20" />
                    <label htmlFor="terms" className="text-[10px] text-gray-400">I agree to the terms & policy</label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 bg-[#c8a96e] text-[#111] font-medium text-xs tracking-wider rounded-lg hover:bg-[#b0945b] transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '...' : 'Signup'}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-600 before:flex-1 before:h-[1px] before:bg-white/10 after:flex-1 after:h-[1px] after:bg-white/10">
                  or
                </div>

                <div className="flex gap-3 mt-6">
                  <button className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#333] bg-[#1a1a1a] text-white rounded-lg text-[11px] hover:bg-white/5 transition-colors">
                    <span className="font-bold text-white">G</span> Google
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#333] bg-[#1a1a1a] text-white rounded-lg text-[11px] hover:bg-white/5 transition-colors">
                    <span className="text-white text-lg leading-none mb-1"></span> Apple
                  </button>
                </div>

                <p className="mt-8 text-center text-xs text-gray-500">
                  Have an account? <button onClick={() => { setView('login'); setError('') }} className="text-[#c8a96e] hover:underline font-medium">Sign In</button>
                </p>
              </>
            )}

            {view === 'otp' && (
              <>
                <h2 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>Verification</h2>
                <p className="text-xs text-gray-400 mb-8 lowercase tracking-wide">Enter the 6-digit code sent to your email</p>

                {error && <div className="mb-4 text-xs text-[#c8a96e] bg-[#c8a96e]/10 border border-[#c8a96e]/20 p-3 rounded">{error}</div>}

                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Security Code</label>
                    <input
                      type="text"
                      maxLength="6"
                      required
                      placeholder="000000"
                      className="w-full px-3 py-3 text-center tracking-[1em] text-lg bg-[#1a1a1a] text-white border border-[#333] rounded-lg focus:outline-none focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e]/20 transition-all placeholder:text-gray-700"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otp.length < 6}
                    className="w-full py-2.5 bg-[#c8a96e] text-[#111] font-medium text-xs tracking-wider rounded-lg hover:bg-[#b0945b] transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '...' : 'Verify & Continue'}
                  </button>
                </form>

                <p className="mt-8 text-center text-xs text-gray-500">
                  Didn't receive code? <button className="text-[#c8a96e] hover:underline font-medium">Resend</button>
                </p>
              </>
            )}
          </div>
        </div>

        {/* Media Section - Right Half */}
        <div className="hidden md:block md:w-1/2 relative bg-black border-l border-[#333]">
          <div className="absolute inset-0">
            <div className="w-full h-full relative">
              {/* Replace src with your local ring video path like /assets/ring.mp4 */}
              <video
                src="/login-videgith"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 pointer-events-none" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AuthModal;
